<script lang="ts">
  /**
   * Gantt view of the Bousfield-DAG — the TIME dimension.
   *   overview → tracks as swimlanes (state-coloured strips)
   *   focused  → a track's milestones scheduled from its dependency DAG
   *              (start = max prereq end; fallback = sequence order).
   * Durations are placeholder units until audits accrue real run-times.
   */
  import type { TaskMilestone } from './api.svelte'
  import { expansion, visibleSubtree, toggleExpand, isExpandable, treeState, selectMilestone } from './taskGraphStore.svelte'

  type Track = { way_id: number; slug: string; name: string; refs: number[]; c: Record<string, number>; pct: number }
  type Model = {
    M: Map<number, TaskMilestone>; tracks: Track[]; bySlug: Map<string, Track>
    depEdges: { from_id: number; to_id: number }[]
  }
  let { model, slug = null, onpicktrack, onpickms }:
    { model: Model; slug: string | null; onpicktrack?: (s: string) => void; onpickms?: (id: number) => void } = $props()

  const COLOR: Record<string, string> = {
    done: '#3fb950', active: '#58a6ff', next: '#d29922', waiting: '#db6d28', planned: '#5b6472',
  }
  function barColor(kind: string, state: string): string {
    if (kind === 'way') return '#8957e5'
    return COLOR[state] || '#5b6472'
  }
  const ROW = 26, LABEL = 230, PAD = 14, COLW = 46, INDENT = 13

  function lanes(): { way_id: number; slug: string; name: string; pct: number; cells: string[] }[] {
    if (slug || !model) return []
    return [...model.tracks].sort((a, b) => b.pct - a.pct).map((t) => ({
      way_id: t.way_id, slug: t.slug, name: t.name, pct: t.pct,
      cells: t.refs.map((id) => model.M.get(id)?.state || 'planned'),
    }))
  }

  type Row = {
    key: string; title: string; state: string; start: number; row: number
    depth: number; kind: string; expandable: boolean; open: boolean; mid?: number; tid?: string
  }
  /* Per-track schedule + expand-in-place: each milestone is a row scheduled from
   * its dependency DAG; an OPEN milestone inserts its decomposition (sub-process
   * + steps from the shared tree) as indented rows beneath it, cascading right.
   * Reads expansion.open + the tree so it recomputes reactively on expand. */
  function schedule(): { rows: Row[]; links: { x1: number; y1: number; x2: number; y2: number }[]; span: number; nowX: number } {
    const empty = { rows: [] as Row[], links: [], span: 1, nowX: LABEL }
    if (!slug || !model) return empty
    const t = model.bySlug.get(slug)
    if (!t) return empty
    const inTrack = new Set(t.refs)
    const prereqs = new Map<number, number[]>()
    for (const d of model.depEdges) {
      if (!inTrack.has(d.from_id) || !inTrack.has(d.to_id)) continue
      const arr = prereqs.get(d.to_id)
      if (arr) arr.push(d.from_id); else prereqs.set(d.to_id, [d.from_id])
    }
    const start = new Map<number, number>()
    const rows: Row[] = []
    const msRow = new Map<number, number>()
    t.refs.forEach((id, i) => {
      const m = model.M.get(id)
      if (!m) return
      const ps = prereqs.get(id) || []
      let s = i
      if (ps.length) { s = 0; for (const p of ps) s = Math.max(s, (start.get(p) ?? 0) + 1) }
      start.set(id, s)
      msRow.set(id, rows.length)
      rows.push({ key: 'm' + id, title: m.title || ('#' + id), state: m.state || 'planned', start: s, row: rows.length,
        depth: 0, kind: 'milestone', expandable: isExpandable('n:' + id), open: expansion.open.has('n:' + id), mid: id })
      if (expansion.open.has('n:' + id)) {
        for (const v of visibleSubtree('n:' + id)) {
          rows.push({ key: 't' + v.rec.id, title: v.rec.label, state: v.rec.state ?? '', start: s + v.depth + 1, row: rows.length,
            depth: v.depth + 1, kind: v.rec.kind, expandable: v.hasKids, open: v.open, tid: v.rec.id })
        }
      }
    })
    const links: { x1: number; y1: number; x2: number; y2: number }[] = []
    for (const d of model.depEdges) {
      const a = msRow.get(d.from_id), b = msRow.get(d.to_id)
      if (a == null || b == null) continue
      const ra = rows[a], rb = rows[b]
      links.push({
        x1: LABEL + ra.start * COLW + COLW - 6, y1: PAD + a * ROW + ROW / 2,
        x2: LABEL + rb.start * COLW, y2: PAD + b * ROW + ROW / 2,
      })
    }
    let span = 1
    for (const r of rows) span = Math.max(span, r.start + 1)
    const firstOpen = rows.find((r) => r.kind === 'milestone' && r.state !== 'done')
    const nowX = LABEL + (firstOpen ? firstOpen.start : span) * COLW
    return { rows, links, span, nowX }
  }

  // click a gantt row: expand if it has depth, else open the detail panel
  function onRow(r: Row) {
    const tid = r.mid != null ? 'n:' + r.mid : r.tid
    if (tid && isExpandable(tid)) { toggleExpand(tid); return }
    if (r.mid != null) { onpickms?.(r.mid); return }
    if (r.tid && r.tid.startsWith('n:')) {
      const rec = treeState.byId.get(r.tid)
      void selectMilestone({ id: Number(r.tid.slice(2)), title: rec?.label ?? '', state: rec?.state ?? null,
        project: null, description: null, completed_at: null, waiting_on: null, has_embedding: true })
    }
  }

  const laneList = $derived(lanes())
  const sched = $derived(schedule())
</script>

<div class="gantt">
  {#if !slug}
    <div class="g-head">PORTFOLIO · {laneList.length} tracks · sequence swimlanes
      <span class="g-sub">click a lane to schedule it</span></div>
    <div class="lanes">
      {#each laneList as l (l.way_id)}
        <button class="lane" onclick={() => onpicktrack?.(l.slug)}>
          <span class="lane-name" title={l.name}>{l.slug}</span>
          <span class="lane-strip">
            {#each l.cells as st, i (i)}<i style="background:{COLOR[st] || COLOR.planned}"></i>{/each}
          </span>
          <span class="lane-pct">{l.pct}%</span>
        </button>
      {/each}
    </div>
  {:else}
    <div class="g-head">{model.bySlug.get(slug)?.name} · scheduled from dependency DAG
      <span class="g-sub">▏now · red = prerequisite · ▸ click to reveal skills/steps · widths are placeholder</span></div>
    <div class="g-scroll">
      <svg width={LABEL + sched.span * COLW + PAD * 2} height={PAD * 2 + sched.rows.length * ROW}>
        <line x1={sched.nowX} y1={PAD - 6} x2={sched.nowX} y2={PAD + sched.rows.length * ROW} stroke="#39d0d8" stroke-dasharray="3 3" />
        {#each sched.links as lk, i (i)}
          <path d="M{lk.x1} {lk.y1} C{lk.x1 + 16} {lk.y1}, {lk.x2 - 16} {lk.y2}, {lk.x2} {lk.y2}" fill="none" stroke="#f85149" stroke-width="1.2" opacity="0.7" />
        {/each}
        {#each sched.rows as r (r.key)}
          <g class="bar" onclick={() => onRow(r)} role="button" tabindex="-1">
            <text x={6 + r.depth * INDENT} y={PAD + r.row * ROW + ROW / 2 + 4} text-anchor="start"
              class="b-label" class:b-step={r.depth > 0}>{r.expandable ? (r.open ? '▾ ' : '▸ ') : ''}{r.title.slice(0, 30)}</text>
            <rect x={LABEL + r.start * COLW} y={PAD + r.row * ROW + 4} width={COLW - 6} height={ROW - 8} rx="3" fill={barColor(r.kind, r.state)} />
          </g>
        {/each}
      </svg>
    </div>
  {/if}
</div>

<style>
  .gantt { position: absolute; inset: 0; overflow: hidden; display: flex; flex-direction: column;
    font-family: var(--font-mono, ui-monospace, monospace); color: #e6edf3; background: #0d1117; }
  .g-head { padding: 8px 14px; font-size: 11px; color: #b9c4d0; border-bottom: 1px solid #2a313c; letter-spacing: .04em; }
  .g-sub { color: #6e7681; font-size: 10px; margin-left: 6px; }
  .lanes { flex: 1; overflow-y: auto; padding: 8px 14px; }
  .lane { display: flex; align-items: center; gap: 10px; width: 100%; background: none; border: none;
    color: inherit; cursor: pointer; padding: 3px 4px; border-radius: 4px; }
  .lane:hover { background: #161b22; }
  .lane-name { width: 200px; min-width: 200px; text-align: right; font-size: 11px; white-space: nowrap;
    overflow: hidden; text-overflow: ellipsis; color: #c9d4e0; }
  .lane-strip { flex: 1; display: flex; height: 16px; border-radius: 3px; overflow: hidden; gap: 1px; background: #0d1117; }
  .lane-strip i { display: block; height: 100%; flex: 1; }
  .lane-pct { width: 34px; font-size: 10px; color: #6e7681; text-align: right; }
  .g-scroll { flex: 1; overflow: auto; }
  .b-label { fill: #8b949e; font-size: 10.5px; }
  .b-step { fill: #6e7681; font-size: 9.5px; }
  .bar { cursor: pointer; }
  .bar:hover rect { stroke: #39d0d8; stroke-width: 2; }
</style>
